import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import {
    Box,
    HStack,
    Link,
    SimpleGrid,
    Text,
    VStack,
} from "@chakra-ui/react";
import {
    HiChatBubbleLeftRight,
    HiDocumentText,
    HiOutlineCpuChip,
    HiPhone,
    HiQuestionMarkCircle,
    HiUser,
} from "react-icons/hi2";
import InfoField from "../InfoField";

const extractInteractionData = (collectedAnswers) => {
    try {
        const answers = typeof collectedAnswers === "string"
            ? JSON.parse(collectedAnswers || "[]")
            : (collectedAnswers ?? []);
        const sorted = [...answers].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

        const displayAnswers = sorted
            .filter(a => a.display_name && a.value !== "__FORM_ACTION_RESUMED__" && a.value !== "uploaded");

        const qCompleteIdx = sorted.findIndex(a => a.name === "@questionsComplete");
        const dCompleteIdx = sorted.findIndex(a => a.name === "@documentsComplete");
        const qEnd = qCompleteIdx !== -1 ? qCompleteIdx : sorted.length;

        const displayQA = sorted
            .slice(0, qEnd)
            .filter(a => !a.display_name && !a.name.startsWith("@"));

        const displayDocs = qCompleteIdx !== -1 && dCompleteIdx !== -1
            ? sorted.slice(qCompleteIdx + 1, dCompleteIdx).filter(a => !a.display_name && !a.name.startsWith("@"))
            : [];

        return { displayAnswers, displayQA, displayDocs };
    } catch {
        return { displayAnswers: [], displayQA: [], displayDocs: [] };
    }
};

const CandidateAiDataTab = ({ candidate, onOpenDocument }) => {
    const { t } = useTranslation("models");
    const fullName = [candidate.firstname, candidate.lastname].filter(Boolean).join(" ") || "-";

    return (
        <VStack gap={4} align="stretch">
            <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
                {/* Personal data */}
                <Box bg="bg.card" border="1px solid" borderColor="border.primary" borderRadius={12} p={5}>
                    <HStack gap={3} mb={4}>
                        <Box p={2} bg="bg.secondary" borderRadius={8} color="brand.400"><HiUser /></Box>
                        <Text fontWeight={700} color="white">{t("candidateDetailPage.sectionPersonalData")}</Text>
                    </HStack>
                    <SimpleGrid columns={2} gap={4}>
                        <InfoField label={t("candidateDetailPage.fieldFirstname")}>
                            <Text color="white" fontSize="sm">{candidate.firstname ?? "-"}</Text>
                        </InfoField>
                        <InfoField label={t("candidateDetailPage.fieldLastname")}>
                            <Text color="white" fontSize="sm">{candidate.lastname ?? "-"}</Text>
                        </InfoField>
                        <InfoField label={t("candidateDetailPage.fieldFullName")}>
                            <Text color="white" fontSize="sm">{fullName}</Text>
                        </InfoField>
                        <InfoField label={t("candidateDetailPage.fieldLanguage")}>
                            <Text color="white" fontSize="sm">{candidate.language ?? "-"}</Text>
                        </InfoField>
                    </SimpleGrid>
                </Box>
                {/* Contact data */}
                <Box bg="bg.card" border="1px solid" borderColor="border.primary" borderRadius={12} p={5}>
                    <HStack gap={3} mb={4}>
                        <Box p={2} bg="bg.secondary" borderRadius={8} color="brand.400"><HiPhone /></Box>
                        <Text fontWeight={700} color="white">{t("candidateDetailPage.sectionContactData")}</Text>
                    </HStack>
                    <VStack align="stretch" gap={4}>
                        <InfoField label={t("candidateDetailPage.fieldPhone")}>
                            {candidate.phoneNumber
                                ? <Link href={`tel:${candidate.phoneNumber}`} color="white" fontSize="sm">{candidate.phoneNumber}</Link>
                                : <Text color="white" fontSize="sm">-</Text>}
                        </InfoField>
                        <InfoField label={t("candidateDetailPage.fieldEmail")}>
                            {candidate.email
                                ? <Link href={`mailto:${candidate.email}`} color="white" fontSize="sm">{candidate.email}</Link>
                                : <Text color="white" fontSize="sm">-</Text>}
                        </InfoField>
                        <InfoField label={t("candidateDetailPage.fieldLocation")}>
                            <Text color="white" fontSize="sm">{candidate.address?.city ?? "-"}</Text>
                        </InfoField>
                    </VStack>
                </Box>
            </SimpleGrid>

            {/* Documents */}
            {candidate.documents?.length > 0 && (
                <Box bg="bg.card" border="1px solid" borderColor="border.primary" borderRadius={12} p={5}>
                    <HStack gap={3} mb={4}>
                        <Box p={2} bg="bg.secondary" borderRadius={8} color="brand.400"><HiDocumentText /></Box>
                        <Text fontWeight={700} color="white">{t("candidateDetailPage.sectionDocuments")}</Text>
                    </HStack>
                    <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
                        {candidate.documents.map((doc, i) => (
                            <InfoField key={i} label={doc.name}>
                                {doc.resource?.path
                                    ? (
                                        <Text
                                            color="brand.400"
                                            fontSize="sm"
                                            cursor="pointer"
                                            onClick={() => onOpenDocument({ path: doc.resource.path, name: doc.name })}
                                        >
                                            Podgląd
                                        </Text>
                                    )
                                    : <Text color="white" fontSize="sm">{doc.value ?? "-"}</Text>}
                            </InfoField>
                        ))}
                    </SimpleGrid>
                </Box>
            )}

            {/* AI Summary */}
            {candidate.summary && (
                <Box bg="bg.card" border="1px solid" borderColor="border.primary" borderRadius={12} p={5}>
                    <HStack gap={3} mb={4}>
                        <Box p={2} bg="bg.secondary" borderRadius={8} color="brand.400"><HiOutlineCpuChip /></Box>
                        <Text fontWeight={700} color="white">{t("candidateDetailPage.sectionAiSummary")}</Text>
                    </HStack>
                    <Text color="white" fontSize="sm" lineHeight={1.7}>{candidate.summary}</Text>
                </Box>
            )}

            {/*/!* Questions *!/*/}
            {/*{candidate.questions?.length > 0 && (*/}
            {/*    <Box bg="bg.card" border="1px solid" borderColor="border.primary" borderRadius={12} p={5}>*/}
            {/*        <HStack gap={3} mb={4}>*/}
            {/*            <Box p={2} bg="bg.secondary" borderRadius={8} color="brand.400"><HiQuestionMarkCircle /></Box>*/}
            {/*            <Text fontWeight={700} color="white">{t("candidateDetailPage.sectionQuestions")}</Text>*/}
            {/*        </HStack>*/}
            {/*        <VStack align="stretch" gap={0}>*/}
            {/*            {candidate.questions.map((q, i) => (*/}
            {/*                <Box*/}
            {/*                    key={i}*/}
            {/*                    py={4}*/}
            {/*                    borderBottom={i < candidate.questions.length - 1 ? "1px solid" : "none"}*/}
            {/*                    borderColor="border.primary"*/}
            {/*                >*/}
            {/*                    <Text color="text.grey" fontSize="2xs" fontWeight={600} textTransform="uppercase" letterSpacing="wider" mb={1}>*/}
            {/*                        {t("candidateDetailPage.questionLabel", { number: i + 1 })}*/}
            {/*                    </Text>*/}
            {/*                    <Text color="text.grey" fontSize="sm" mb={2}>{q.content ?? q.name}</Text>*/}
            {/*                    <Box h="1px" bg="border.primary" mb={2} />*/}
            {/*                    <Text color="white" fontSize="sm" fontWeight={500}>*/}
            {/*                        {q.answer ?? t("candidateDetailPage.noAnswer")}*/}
            {/*                    </Text>*/}
            {/*                </Box>*/}
            {/*            ))}*/}
            {/*        </VStack>*/}
            {/*    </Box>*/}
            {/*)}*/}

            {/* Interactions */}
            {candidate.interactions?.length > 0 && (
                <Box bg="bg.card" border="1px solid" borderColor="border.primary" borderRadius={12} p={5}>
                    <HStack gap={3} mb={4}>
                        <Box p={2} bg="bg.secondary" borderRadius={8} color="brand.400"><HiChatBubbleLeftRight /></Box>
                        <Text fontWeight={700} color="white">{t("candidateDetailPage.sectionInteractions")}</Text>
                    </HStack>
                    <VStack align="stretch" gap={3}>
                        {candidate.interactions.map((interaction) => {
                            const { displayAnswers, displayQA, displayDocs } = extractInteractionData(interaction.collectedAnswers);
                            const hasAnyData = displayAnswers.length > 0 || displayQA.length > 0 || displayDocs.length > 0;
                            return (
                                <Box
                                    key={interaction.interactionId}
                                    bg="bg.secondary"
                                    border="1px solid"
                                    borderColor="border.primary"
                                    borderRadius={8}
                                    p={4}
                                >
                                    <HStack justify="space-between" mb={3}>
                                        <Text color="white" fontWeight={600} fontSize="sm">
                                            {interaction.title || t("candidateDetailPage.untitledInteraction")}
                                        </Text>
                                        <Text color="text.grey" fontSize="xs">
                                            {dayjs(interaction.createdAt).format("DD.MM.YYYY HH:mm")}
                                        </Text>
                                    </HStack>

                                    {!hasAnyData && (
                                        <Text color="text.grey" fontSize="sm">{t("candidateDetailPage.noAnswer")}</Text>
                                    )}

                                    {displayAnswers.length > 0 && (
                                        <Box mb={displayQA.length > 0 || displayDocs.length > 0 ? 4 : 0}>
                                            <Text color="text.grey" fontSize="2xs" fontWeight={600} textTransform="uppercase" letterSpacing="wider" mb={2}>
                                                {t("conversationDetailPage.answersTitle")}
                                            </Text>
                                            <SimpleGrid columns={{ base: 1, md: 2 }} gap={3}>
                                                {displayAnswers.map((a) => (
                                                    <InfoField key={a.name} label={a.display_name}>
                                                        <Text color="white" fontSize="sm">{a.value}</Text>
                                                    </InfoField>
                                                ))}
                                            </SimpleGrid>
                                        </Box>
                                    )}

                                    {displayQA.length > 0 && (
                                        <Box mb={displayDocs.length > 0 ? 4 : 0}>
                                            <Text color="text.grey" fontSize="2xs" fontWeight={600} textTransform="uppercase" letterSpacing="wider" mb={2}>
                                                {t("conversationDetailPage.qaTitle")}
                                            </Text>
                                            <Box bg="bg.card" border="1px solid" borderColor="border.primary" borderRadius={8} overflow="hidden">
                                                {displayQA.map((item, i) => (
                                                    <Box
                                                        key={item.name}
                                                        px={4}
                                                        py={3}
                                                        borderBottom={i < displayQA.length - 1 ? "1px solid" : "none"}
                                                        borderColor="border.primary"
                                                    >
                                                        <Text color="text.grey" fontSize="sm" mb={1}>{item.name}</Text>
                                                        <Text color="white" fontSize="sm" fontWeight={500}>{item.value}</Text>
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Box>
                                    )}

                                    {displayDocs.length > 0 && (
                                        <Box>
                                            <Text color="text.grey" fontSize="2xs" fontWeight={600} textTransform="uppercase" letterSpacing="wider" mb={2}>
                                                {t("conversationDetailPage.docsTitle")}
                                            </Text>
                                            <Box bg="bg.card" border="1px solid" borderColor="border.primary" borderRadius={8} overflow="hidden">
                                                {displayDocs.map((item, i) => (
                                                    <Box
                                                        key={item.name}
                                                        px={4}
                                                        py={3}
                                                        borderBottom={i < displayDocs.length - 1 ? "1px solid" : "none"}
                                                        borderColor="border.primary"
                                                    >
                                                        <Text color="text.grey" fontSize="sm" mb={1}>{item.name}</Text>
                                                        <Text color="white" fontSize="sm" fontWeight={500}>{item.value}</Text>
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            );
                        })}
                    </VStack>
                </Box>
            )}
        </VStack>
    );
};

export default CandidateAiDataTab;
